import {statusButtonTypes} from 'components/DSYS/StatusButtonIcon';
import {StatusChip} from 'components/DSYS/StatusChip';
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';
import {type ReactNode} from 'react';

export type SerializedStatusChipNode = Spread<
  {
    status: string;
  },
  SerializedLexicalNode
>;

/** Matches a `/status/{value}` pseudo-route, capturing the status value. */
const STATUS_HREF_PATTERN = /^\/status\/([a-z_]+)/;

/**
 * Translate the workstream status values that are NOT already chip tokens onto
 * the canonical chip vocabulary — i.e. the data-layer spellings the chip doesn't
 * know natively (deliverable `completed`, metric `behind`, and the `canceled`
 * misspelling). The data-vocabulary analog of the view-local `getMetricButtonStatus`.
 * Returns `null` for anything unrecognized.
 */
function translateDataStatus(value: string): string | null {
  switch (value) {
    case 'completed':
      return 'complete';
    case 'behind':
      return 'off_track';
    case 'canceled':
      return 'cancelled';
    default:
      return null;
  }
}

/**
 * Resolve any status string onto the canonical chip vocabulary rendered by
 * `StatusChip`. If it's already a chip token (`statusButtonTypes`) use it as-is;
 * otherwise run it through {@link translateDataStatus}. Returns `null` when it
 * isn't a status at all, so callers can use it as a guard for free text (e.g. an
 * AI answer Markdown table cell).
 */
function getStatusChipVariant(value: string): string | null {
  const key = value.trim().toLowerCase();
  if ((statusButtonTypes as readonly string[]).includes(key)) {
    return key;
  }
  return translateDataStatus(key);
}

/** True when `value` is a known workstream/metric status the chip can render. */
export function isStatusValue(value: string): boolean {
  return getStatusChipVariant(value) !== null;
}

/** Pseudo-route a status chip serializes to (carries the value through Markdown). */
const statusChipHref = (status: string): string => `/status/${status}`;

function $convertStatusAnchor(domNode: HTMLElement): DOMConversionOutput {
  const href = domNode.getAttribute('href') ?? '';
  const status = STATUS_HREF_PATTERN.exec(href)?.[1] ?? '';
  return {node: $createStatusChipNode(status)};
}

/**
 * Inline decorator node for a workstream/metric status. Built programmatically by
 * {@link MarkdownView} for a known status value, whether it appears as a
 * `/status/{value}` link (in a table cell or prose) or as a bare value in a table
 * cell. Unknown values fall back to plain text.
 */
export class StatusChipNode extends DecoratorNode<ReactNode> {
  __status: string;

  static getType(): string {
    return 'status-chip';
  }

  static clone(node: StatusChipNode): StatusChipNode {
    return new StatusChipNode(node.__status, node.__key);
  }

  constructor(status: string, key?: NodeKey) {
    super(key);
    this.__status = status;
  }

  isInline(): boolean {
    return true;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'inline-flex';
    span.style.verticalAlign = 'middle';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (domNode: HTMLElement) => {
        const href = domNode.getAttribute('href') ?? '';
        if (!STATUS_HREF_PATTERN.test(href)) {
          return null;
        }
        return {conversion: $convertStatusAnchor, priority: 2};
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('a');
    element.setAttribute('href', statusChipHref(this.__status));
    element.textContent = this.__status;
    return {element};
  }

  static importJSON(serializedNode: SerializedStatusChipNode): StatusChipNode {
    return $createStatusChipNode(serializedNode.status);
  }

  exportJSON(): SerializedStatusChipNode {
    return {
      status: this.__status,
      type: 'status-chip',
      version: 1,
    };
  }

  getTextContent(): string {
    return this.getLatest().__status;
  }

  decorate(): ReactNode {
    return <StatusChip status={this.__status} size="xsmall" />;
  }
}

export function $createStatusChipNode(status: string): StatusChipNode {
  return $applyNodeReplacement(new StatusChipNode(getStatusChipVariant(status) ?? status));
}

export function $isStatusChipNode(node: LexicalNode | null | undefined): node is StatusChipNode {
  return node instanceof StatusChipNode;
}
